import * as React from "react";
import { useMemo } from "react";

interface VideoProps {
    url: string;
    width: number;
    height: number;
    cookieConsent: Record<string, boolean>;
}

/**
 * Matches the provided regex against the provided url
 * and returns the found ID.
 *
 * @param url
 * @param regex
 */
const getId = (url: string, regex: RegExp): string | null => {
    const matches = url.match(regex);

    return matches ? matches[1] : null;
};

/**
 * Converts a provided vimeo URL into an iframe URL.
 * @param url
 */
const vimeo = (url: string): string | null => {
    const id = getId(
        url,
        /https?:\/\/(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d+)/
    );

    return id
        ? `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0&dnt=1`
        : null;
};

/**
 * Converts a provided youtube URL into an iframe URL.
 *
 * @param url
 * @param short
 */
const youtube = (
    url: string,
    short = isShortYoutubeLink(url)
): string | null => {
    const id = getId(
        url,
        short
            ? /https?:\/\/(?:www\.)?youtu\.be\/([\w-]+)/
            : /https?:\/\/(?:www\.)?youtube\.com\/watch\/?\?v=([\w-]+)/
    );

    return id
        ? `https://www.youtube-nocookie.com/embed/${id}?modestbranding=1`
        : null;
};

/**
 * Helpers to determine what the provided url is.
 */
const isShortYoutubeLink = (url: string) =>
    url.includes("youtu.be") && !url.includes("feature=youtu.be");
const isYoutubeLink = (url: string) =>
    url.includes("youtube.com") || isShortYoutubeLink(url);
const isVimeoLink = (url: string) => url.includes("vimeo.com");

const makeProps = (src: string, width: number, height: number, extra = {}) => ({
    src,
    width,
    height,
    frameBorder: 0,
    allowFullScreen: true,
    webkitallowfullscreen: "true",
    mozallowfullscreen: "true",
    ...extra
});

/**
 * The component.
 */
export const Video: React.FC<VideoProps> = ({
    url,
    width,
    height,
    cookieConsent
}) => {
    const [type, embedUrl] = useMemo(
        () => {
            if (isVimeoLink(url)) {
                return ["vimeo", vimeo(url)];
            }

            if (isYoutubeLink(url)) {
                return ["youtube", youtube(url)];
            }

            return [null, null];
        },
        [url]
    );

    const iframeProps = useMemo(
        () => {
            if (isVimeoLink(url)) {
                return makeProps(embedUrl, width, height);
            }

            if (isYoutubeLink(url)) {
                return makeProps(embedUrl, width, height, {
                    allow:
                        "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                });
            }

            return {};
        },
        [url, embedUrl, width, height]
    );

    return type && cookieConsent[type] && embedUrl ? (
        <iframe {...iframeProps} />
    ) : null;
};

Video.displayName = "Video";
